<template>
  <div
    ref="widgetElement"
    class="enhanced-text-widget pb-mobile-responsive"
    :class="[
      `widget-style-${widget.style || 'default'}`,
      `widget-align-${widget.textAlign || 'left'}`,
      { 'has-icon': widget.icon && !widget.hideIcon },
      { 'has-background-image': isBackgroundMode && widget.backgroundImage },
      { 'is-card-image-mode': isCardMode },
      { [`card-img-pos-${widget.imageCardPosition || 'left'}`]: isCardMode },
      { 'is-visible': isVisible },
    ]"
    :style="widgetStyles"
  >
    <!-- Background Image Layer (background mode only) -->
    <img
      v-if="isBackgroundMode && widget.backgroundImage"
      class="background-image-layer"
      :src="resolvedImageUrl"
      :alt="widget.title || ''"
      aria-hidden="true"
      loading="lazy"
      :style="backgroundImageLayerStyles"
    />

    <!-- Background Image Overlay (background mode only) -->
    <div
      v-if="isBackgroundMode && widget.backgroundImage"
      class="background-overlay"
      :style="overlayStyles"
    ></div>

    <!-- === CARD IMAGE MODE === -->
    <div v-if="isCardMode && widget.backgroundImage" class="widget-card-layout">
      <!-- Image panel -->
      <div class="widget-card-image-panel" :style="cardImagePanelStyles">
        <div class="widget-card-image-inner" :style="cardImageInnerStyles"></div>
      </div>
      <!-- Text panel -->
      <div class="widget-card-text-panel">
        <div v-if="widget.icon && !widget.hideIcon" class="widget-icon" :style="iconStyles">
          <i :class="widget.icon"></i>
        </div>
        <div class="widget-content" :class="{ 'with-icon': widget.icon && !widget.hideIcon }">
          <h5 v-if="widget.title" class="widget-title" :style="titleStyles">
            {{ widget.title }}
          </h5>
          <h6 v-if="widget.subtitle" class="widget-subtitle" :style="subtitleStyles">
            {{ widget.subtitle }}
          </h6>
          <div v-if="processedContent" class="widget-text" v-html="processedContent"></div>
          <div v-if="widget.badge" class="widget-badge" :style="badgeStyles">
            {{ widget.badge }}
          </div>
          <div v-if="widget.buttonText" class="widget-action">
            <a
              v-if="widget.buttonUrl"
              :href="widget.buttonUrl"
              :class="buttonActionClasses"
              :style="actionButtonStyles"
              :target="widget.buttonTarget || '_self'"
            >
              <i v-if="widget.buttonIcon" :class="widget.buttonIcon + ' me-2'"></i>
              {{ widget.buttonText }}
            </a>
            <button
              v-else
              :class="buttonActionClasses"
              :style="actionButtonStyles"
            >
              <i v-if="widget.buttonIcon" :class="widget.buttonIcon + ' me-2'"></i>
              {{ widget.buttonText }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- === NORMAL / BACKGROUND MODE === -->
    <template v-else>
      <!-- Widget Content Container -->
      <div class="widget-inner-content">
        <!-- Icon -->
        <div v-if="widget.icon && !widget.hideIcon" class="widget-icon" :style="iconStyles">
          <i :class="widget.icon"></i>
        </div>

        <!-- Content Container -->
        <div class="widget-content" :class="{ 'with-icon': widget.icon && !widget.hideIcon }">
          <!-- Title -->
          <h5 v-if="widget.title" class="widget-title" :style="titleStyles">
            {{ widget.title }}
          </h5>

          <!-- Subtitle -->
          <h6 v-if="widget.subtitle" class="widget-subtitle" :style="subtitleStyles">
            {{ widget.subtitle }}
          </h6>

          <!-- Main Content -->
          <div v-if="processedContent" class="widget-text" v-html="processedContent"></div>

          <!-- Badge/Number -->
          <div v-if="widget.badge" class="widget-badge" :style="badgeStyles">
            {{ widget.badge }}
          </div>

          <!-- Action Button -->
          <div v-if="widget.buttonText" class="widget-action">
            <a
              v-if="widget.buttonUrl"
              :href="widget.buttonUrl"
              :class="buttonActionClasses"
              :style="actionButtonStyles"
              :target="widget.buttonTarget || '_self'"
            >
              <i v-if="widget.buttonIcon" :class="widget.buttonIcon + ' me-2'"></i>
              {{ widget.buttonText }}
            </a>
            <button
              v-else
              :class="buttonActionClasses"
              :style="actionButtonStyles"
            >
              <i v-if="widget.buttonIcon" :class="widget.buttonIcon + ' me-2'"></i>
              {{ widget.buttonText }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import type { Widget } from '@/types/pagebuilder'
import { resolveWidgetBackgroundStyle } from '@/utils/backgroundGradient'
import { processRichTextContent } from '@/utils/textWidgetUtils'
import { UPLOADS_BASE_URL } from '@/config/env'
import {
  assignResponsiveCssVar,
  normalizeSizeValue,
} from '@/utils/responsiveWidgetSize'

interface EnhancedTextWidget {
  id: string
  type: 'enhanced-text'
  order: number
  title?: string
  subtitle?: string
  content?: string
  icon?: string
  iconColor?: string
  iconSize?: string
  iconBackground?: string
  hideIcon?: boolean
  badge?: string
  badgeColor?: string
  badgeBackground?: string
  buttonText?: string
  buttonUrl?: string
  buttonIcon?: string
  buttonStyle?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'danger'
    | 'warning'
    | 'info'
    | 'light'
    | 'dark'
    | 'outline-primary'
    | 'custom'
  buttonSize?: 'sm' | 'md' | 'lg'
  buttonTarget?: '_self' | '_blank'
  buttonBackgroundColor?: string
  buttonTextColor?: string
  buttonBorderColor?: string
  style?: 'default' | 'card' | 'highlight' | 'minimal' | 'bordered' | 'gradient'
  fontSize?: string
  titleSize?: string
  subtitleSize?: string
  textAlign?: 'left' | 'center' | 'right'
  textColor?: string
  titleColor?: string
  subtitleColor?: string
  backgroundColor?: string
  backgroundFillMode?: 'color' | 'gradient'
  gradientType?: 'linear' | 'radial'
  gradientAngle?: number
  gradientColor1?: string
  gradientColor2?: string
  gradientColor1Location?: number
  gradientColor2Location?: number
  borderColor?: string
  borderRadius?: string
  width?: string
  maxWidth?: string
  minHeight?: string
  height?: string
  padding?: string
  margin?: string
  shadow?: boolean
  backgroundType?: 'color' | 'image'
  backgroundImage?: string
  imageMode?: 'background' | 'card'
  // Background mode
  backgroundSize?: 'cover' | 'contain' | 'auto'
  backgroundPosition?: 'center' | 'top' | 'bottom' | 'left' | 'right'
  backgroundRepeat?: 'no-repeat' | 'repeat' | 'repeat-x' | 'repeat-y'
  /** Min height of the background image area (e.g. 200px) */
  backgroundImageHeight?: string
  backgroundImageHeightMobile?: string
  fontSizeMobile?: string
  paddingMobile?: string
  titleSizeMobile?: string
  subtitleSizeMobile?: string
  overlayColor?: string
  overlayOpacity?: number
  // Card mode
  imageCardPosition?: 'left' | 'right' | 'top' | 'bottom'
  imageCardSize?: string
  imageCardFit?: 'cover' | 'contain' | 'auto'
}

interface Props {
  widget: EnhancedTextWidget
}

const props = defineProps<Props>()

// Intersection Observer for fade-in animation
const widgetElement = ref<HTMLElement | null>(null)
const isVisible = ref(false)

onMounted(() => {
  if (!widgetElement.value) return

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          isVisible.value = true
          observer.disconnect()
        }
      })
    },
    {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    },
  )

  observer.observe(widgetElement.value)

  onUnmounted(() => {
    observer.disconnect()
  })
})

const isCardMode = computed(
  () => props.widget.backgroundType === 'image' && props.widget.imageMode === 'card',
)

const isBackgroundMode = computed(
  () => props.widget.backgroundType === 'image' && props.widget.imageMode !== 'card',
)

const buttonActionClasses = computed(() => {
  const classes = ['btn', 'widget-btn']
  const style = props.widget.buttonStyle || 'primary'
  const usesCustom =
    style === 'custom' ||
    !!(
      props.widget.buttonBackgroundColor ||
      props.widget.buttonTextColor ||
      props.widget.buttonBorderColor
    )

  if (!usesCustom) {
    classes.push(`btn-${style}`)
  }

  const size = props.widget.buttonSize
  if (size && size !== 'md') {
    classes.push(`btn-${size}`)
  }

  return classes
})

const actionButtonStyles = computed(() => {
  const styles: Record<string, string> = {}

  if (props.widget.buttonBackgroundColor) {
    styles.backgroundColor = `${props.widget.buttonBackgroundColor} !important`
  }
  if (props.widget.buttonTextColor) {
    styles.color = `${props.widget.buttonTextColor} !important`
  }
  if (props.widget.buttonBorderColor) {
    styles.borderColor = `${props.widget.buttonBorderColor} !important`
    styles.border = `2px solid ${props.widget.buttonBorderColor} !important`
  } else if (props.widget.buttonBackgroundColor) {
    styles.border = `2px solid ${props.widget.buttonBackgroundColor} !important`
  }

  return styles
})

// Resolve the image URL (shared helper)
const resolvedImageUrl = computed(() => {
  let imageUrl = props.widget.backgroundImage || ''
  if (!imageUrl) return ''
  if (!imageUrl.startsWith('blob:') && !imageUrl.startsWith('http')) {
    imageUrl = imageUrl.includes('/')
      ? `${UPLOADS_BASE_URL}/${imageUrl}`
      : `${UPLOADS_BASE_URL}/imgs/images/${imageUrl}`
  }
  return imageUrl
})

const normalizeSize = (raw?: string, fallback?: string): string | undefined => {
  return normalizeSizeValue(raw) ?? (fallback ? normalizeSizeValue(fallback) : undefined)
}

const effectiveBackgroundHeight = computed(() => {
  if (!isBackgroundMode.value || !props.widget.backgroundImage) return undefined
  if (props.widget.height) return normalizeSize(props.widget.height)
  if (props.widget.minHeight) return normalizeSize(props.widget.minHeight)
  const bgH = props.widget.backgroundImageHeight
  if (!bgH || bgH === 'auto') return undefined
  return normalizeSize(bgH, '280px')
})

const backgroundObjectPosition = computed(() => {
  const pos = props.widget.backgroundPosition || 'center'
  const map: Record<string, string> = {
    center: 'center center',
    top: 'center top',
    bottom: 'center bottom',
    left: 'left center',
    right: 'right center',
  }
  return map[pos] || pos
})

const backgroundObjectFit = computed((): 'cover' | 'contain' => {
  const fit = props.widget.backgroundSize || 'cover'
  return fit === 'contain' ? 'contain' : 'cover'
})

const backgroundImageLayerStyles = computed((): Record<string, string> => ({
  objectFit: backgroundObjectFit.value,
  objectPosition: backgroundObjectPosition.value,
}))

const widgetStyles = computed(() => {
  const styles: Record<string, string> = {
    textAlign: props.widget.textAlign || 'left',
    color: props.widget.textColor || '#333',
    '--widget-text-color': props.widget.textColor || '#333',
    margin: props.widget.margin || '0 0 2rem 0',
    borderRadius: props.widget.borderRadius || '8px',
  }

  assignResponsiveCssVar(styles, '--pb-font-size', props.widget.fontSize || '1rem', props.widget.fontSizeMobile)
  assignResponsiveCssVar(styles, '--pb-padding', props.widget.padding || '1.5rem', props.widget.paddingMobile)
  assignResponsiveCssVar(styles, '--pb-title-size', props.widget.titleSize || '1.5rem', props.widget.titleSizeMobile)
  assignResponsiveCssVar(
    styles,
    '--pb-subtitle-size',
    props.widget.subtitleSize || '1rem',
    props.widget.subtitleSizeMobile,
  )

  if (isBackgroundMode.value && props.widget.backgroundImage) {
    styles.width = props.widget.width || '100%'
    styles.maxWidth = props.widget.maxWidth || props.widget.width || '100%'
    styles.display = 'block'
    styles.boxSizing = 'border-box'
  } else {
    if (props.widget.width) styles.width = props.widget.width
    if (props.widget.maxWidth) styles.maxWidth = props.widget.maxWidth
  }

  if (
    isBackgroundMode.value &&
    props.widget.backgroundImage &&
    props.widget.width &&
    String(props.widget.width).includes('%') &&
    parseFloat(String(props.widget.width)) < 100
  ) {
    styles.marginLeft = 'auto'
    styles.marginRight = 'auto'
  }

  if (!isCardMode.value && props.widget.height && !(isBackgroundMode.value && props.widget.backgroundImage)) {
    styles.height = normalizeSize(props.widget.height)!
  }
  if (!isCardMode.value && props.widget.minHeight && !(isBackgroundMode.value && props.widget.backgroundImage)) {
    styles.minHeight = normalizeSize(props.widget.minHeight)!
  }

  // Background handling: Image or Color
  if (props.widget.backgroundType === 'image' && props.widget.backgroundImage && !isCardMode.value) {
    const bgHeight = effectiveBackgroundHeight.value
    assignResponsiveCssVar(
      styles,
      '--pb-bg-image-height',
      bgHeight,
      props.widget.backgroundImageHeightMobile,
      false,
    )
    if (props.widget.height) {
      styles.height = normalizeSize(props.widget.height)!
      styles.minHeight = styles.height
    } else if (bgHeight) {
      styles.height = `var(--pb-bg-image-height)`
      styles.minHeight = `var(--pb-bg-image-height)`
    }
    // Make text white by default for better readability on images
    if (!props.widget.textColor) {
      styles.color = '#fff'
    }
  } else if (!isCardMode.value) {
    Object.assign(styles, resolveWidgetBackgroundStyle(props.widget))
  } else {
    Object.assign(styles, resolveWidgetBackgroundStyle(props.widget))
    styles.padding = '0'
  }

  // Add border if color is specified
  if (props.widget.borderColor) {
    styles.border = `1px solid ${props.widget.borderColor}`
  }

  if (props.widget.shadow) {
    styles.boxShadow = '0 2px 10px rgba(0,0,0,0.1)'
  }

  const hasCustomGradient = props.widget.backgroundFillMode === 'gradient'

  // Style presets - can be overridden by custom colors / gradient
  if (props.widget.style === 'card') {
    if (
      !hasCustomGradient &&
      (!props.widget.backgroundColor || props.widget.backgroundColor === 'transparent') &&
      props.widget.backgroundType !== 'image'
    ) {
      styles.backgroundColor = '#fff'
    }
    if (!props.widget.shadow) {
      styles.boxShadow = '0 2px 10px rgba(0,0,0,0.1)'
    }
  } else if (props.widget.style === 'highlight') {
    if (
      !hasCustomGradient &&
      (!props.widget.backgroundColor || props.widget.backgroundColor === 'transparent') &&
      props.widget.backgroundType !== 'image'
    ) {
      styles.backgroundColor = '#f8f9fa'
    }
  } else if (props.widget.style === 'bordered') {
    if (!props.widget.borderColor) {
      styles.border = '1px solid #dee2e6'
    }
  } else if (props.widget.style === 'gradient' && !hasCustomGradient) {
    if (!props.widget.backgroundColor && props.widget.backgroundType !== 'image') {
      styles.background = 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
      styles.color = '#fff'
    }
  }

  return styles
})

const overlayStyles = computed(() => {
  const overlayColor = props.widget.overlayColor || '#000000'
  const overlayOpacity =
    props.widget.overlayOpacity !== undefined ? props.widget.overlayOpacity : 0.5

  // Convert hex to rgba
  const r = parseInt(overlayColor.slice(1, 3), 16)
  const g = parseInt(overlayColor.slice(3, 5), 16)
  const b = parseInt(overlayColor.slice(5, 7), 16)

  return {
    backgroundColor: `rgba(${r}, ${g}, ${b}, ${overlayOpacity})`,
  }
})

// Card image mode computed styles
const cardImagePanelStyles = computed(() => {
  const pos = props.widget.imageCardPosition || 'left'
  const isHorizontal = pos === 'left' || pos === 'right'
  const size = props.widget.imageCardSize || (isHorizontal ? '42%' : '240px')
  if (isHorizontal) {
    return { width: size, flexShrink: '0', minHeight: '200px', overflow: 'hidden' }
  } else {
    return { height: size, width: '100%', flexShrink: '0', overflow: 'hidden' }
  }
})

const cardImageInnerStyles = computed(() => ({
  backgroundImage: `url("${resolvedImageUrl.value}")`,
  backgroundSize: props.widget.imageCardFit || 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  width: '100%',
  height: '100%',
  minHeight: 'inherit',
}))

const iconStyles = computed(() => ({
  color: props.widget.iconColor || '#007bff',
  fontSize: props.widget.iconSize || '2.5rem',
  backgroundColor: props.widget.iconBackground || 'transparent',
  borderRadius: props.widget.iconBackground ? '50%' : '0',
}))

const titleStyles = computed(() => ({
  color: props.widget.titleColor || (props.widget.style === 'gradient' ? '#fff' : '#2c3e50'),
  fontWeight: '600',
  marginBottom: '0.5rem',
}))

const subtitleStyles = computed(() => ({
  color:
    props.widget.subtitleColor ||
    (props.widget.style === 'gradient' ? 'rgba(255,255,255,0.9)' : '#6c757d'),
  fontWeight: '400',
  marginBottom: '1rem',
}))

const badgeStyles = computed(() => ({
  backgroundColor: props.widget.badgeBackground || '#007bff',
  color: props.widget.badgeColor || '#fff',
}))

const processedContent = computed(() => {
  return processRichTextContent(props.widget.content || '', props.widget.textColor)
})
</script>

<style scoped>
.enhanced-text-widget {
  position: relative;
  transition: all 0.3s ease;
  overflow: hidden;
}

/* Background Image Support */
.enhanced-text-widget.has-background-image {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.background-image-layer {
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  pointer-events: none;
  user-select: none;
}

.background-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  pointer-events: none;
}

.widget-inner-content {
  position: relative;
  z-index: 2;
  width: 100%;
}

/* ===== CARD IMAGE MODE ===== */
.is-card-image-mode {
  overflow: hidden;
}

.widget-card-layout {
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: 220px;
}

/* Horizontal layouts */
.is-card-image-mode.card-img-pos-left .widget-card-layout {
  flex-direction: row;
}
.is-card-image-mode.card-img-pos-right .widget-card-layout {
  flex-direction: row-reverse;
}

/* Vertical layouts */
.is-card-image-mode.card-img-pos-top .widget-card-layout {
  flex-direction: column;
}
.is-card-image-mode.card-img-pos-bottom .widget-card-layout {
  flex-direction: column-reverse;
}

.widget-card-image-panel {
  position: relative;
  flex-shrink: 0;
}

.widget-card-image-inner {
  width: 100%;
  height: 100%;
  min-height: 220px;
}

.widget-card-text-panel {
  flex: 1;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
}

@media (max-width: 768px) {
  .is-card-image-mode.card-img-pos-left .widget-card-layout,
  .is-card-image-mode.card-img-pos-right .widget-card-layout {
    flex-direction: column;
  }
  .is-card-image-mode.card-img-pos-left .widget-card-image-panel,
  .is-card-image-mode.card-img-pos-right .widget-card-image-panel {
    width: 100% !important;
    height: 220px !important;
    min-height: unset !important;
  }
  .widget-card-image-inner {
    min-height: 220px;
  }

  .widget-card-layout {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .widget-card-text-panel {
    padding: 1rem;
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .widget-title,
  .widget-subtitle,
  .widget-content {
    overflow-wrap: anywhere;
    word-break: break-word;
    max-width: 100%;
  }
}


.enhanced-text-widget.has-icon .widget-inner-content {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
}

.widget-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.widget-icon i {
  display: block;
}

.widget-content {
  flex: 1;
}

.widget-content.with-icon {
  padding-top: 0.5rem;
}

.widget-title {
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
  font-weight: 600;
}

.widget-subtitle {
  margin: 0 0 1rem 0;
  line-height: 1.4;
  opacity: 0.8;
}

.widget-text {
  line-height: 1.7;
  margin-bottom: 1.5rem;
}

.widget-text:last-child {
  margin-bottom: 0;
}

.widget-badge {
  position: absolute;
  top: -10px;
  right: -10px;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.widget-action {
  margin-top: 1.5rem;
}

.widget-btn {
  font-weight: 500;
  text-decoration: none;
}

/* Style variations */

.widget-style-highlight {
  position: relative;
  overflow: hidden;
}

.widget-style-highlight::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(to bottom, #007bff, #0056b3);
}

.widget-style-minimal {
  border: none;
  background: none;
  padding: 1rem 0;
}

.widget-style-bordered {
  position: relative;
  overflow: hidden;
}

.widget-style-bordered::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #007bff, #0056b3);
}

.widget-style-gradient {
  position: relative;
  overflow: hidden;
}

.widget-style-gradient::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%);
  pointer-events: none;
}

/* Alignment */
.widget-align-center {
  text-align: center;
}

.widget-align-center.has-icon .widget-inner-content {
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.widget-align-center .widget-icon {
  align-self: center;
}

.widget-align-right {
  text-align: right;
}

/* Deep styles for content */
.widget-text :deep(p),
.widget-text :deep(h1),
.widget-text :deep(h2),
.widget-text :deep(h3),
.widget-text :deep(h4),
.widget-text :deep(h5),
.widget-text :deep(h6),
.widget-text :deep(li),
.widget-text :deep(span),
.widget-text :deep(div),
.widget-text :deep(blockquote),
.widget-text :deep(strong),
.widget-text :deep(em) {
  color: var(--widget-text-color, inherit) !important;
}

.widget-text :deep(h1),
.widget-text :deep(h2),
.widget-text :deep(h3),
.widget-text :deep(h4),
.widget-text :deep(h5),
.widget-text :deep(h6) {
  margin-bottom: 0.75rem;
}

.widget-text :deep(p) {
  margin-bottom: 1rem;
}

.widget-text :deep(p:last-child) {
  margin-bottom: 0;
}

.widget-text :deep(ul),
.widget-text :deep(ol) {
  margin-bottom: 1rem;
  padding-left: 1.5rem;
}

.widget-text :deep(li) {
  margin-bottom: 0.5rem;
}

.widget-text :deep(a) {
  color: #007bff;
  text-decoration: none;
}

.widget-text :deep(a:hover) {
  text-decoration: underline;
}

.widget-text :deep(strong) {
  font-weight: 600;
}

.widget-text :deep(blockquote) {
  margin: 1.5rem 0;
  padding: 1rem 1.5rem;
  border-left: 4px solid #007bff;
  background: rgba(0, 123, 255, 0.1);
  font-style: italic;
}

/* Responsive */
@media (max-width: 768px) {
  .enhanced-text-widget.has-icon {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }

  .widget-icon {
    align-self: center;
    width: 3.5rem;
    height: 3.5rem;
  }

  .widget-content.with-icon {
    padding-top: 0;
  }

  .widget-badge {
    position: static;
    display: inline-block;
    margin-bottom: 1rem;
  }

}

@media (max-width: 576px) {
  .enhanced-text-widget.has-icon {
    gap: 0.75rem;
  }

  .widget-icon {
    width: 3rem;
    height: 3rem;
  }

  /* Reduce icon size on very small screens */
  .widget-icon i {
    font-size: inherit; /* Use the size defined in iconStyles */
  }
}

@media (max-width: 480px) {
  .enhanced-text-widget.has-icon {
    gap: 0.5rem;
  }

  .widget-icon {
    width: 2.5rem;
    height: 2.5rem;
  }
}
</style>
